import dayjs from "dayjs";
import { dateFormat, N_CHECKER, SECURITY_GROUPS } from "utils/constants";
import {
  EMAIL_UNIQUENESS_CODES,
  PAYROLL_EXISTS_CODE,
  empMsgCode,
  fieldNames,
  terminatedCode,
  TERMINATED_JOB_TITLE_ID
} from "utils/constants/employee/EmployeeDetailConstant";

export const getFormData = (data) => {
  let formData = {
    [fieldNames.employeeId]: (data && data[fieldNames.employeeId]) || "",
    [fieldNames.firstName]: (data && data[fieldNames.firstName]) || "",
    [fieldNames.middleName]: (data && data[fieldNames.middleName]) || "",
    [fieldNames.lastName]: (data && data[fieldNames.lastName]) || "",
    [fieldNames.employmentStartDate]:
      (data && data[fieldNames.employmentStartDate]) || "",
    [fieldNames.employmentEndDate]:
      (data && data[fieldNames.employmentEndDate]) || "",
    [fieldNames.jobTitleId]: (data && data[fieldNames.jobTitleId]) || "",
    [fieldNames.operationalCenterId]:
      (data && data[fieldNames.operationalCenterId]) || "",
    [fieldNames.businessEmailAddr]:
      (data && data[fieldNames.businessEmailAddr]) || "",

    [fieldNames.businessPhone]: (data && data[fieldNames.businessPhone]) || "",
    [fieldNames.extention]: (data && data[fieldNames.businessPhnExt]) || "",
    [fieldNames.country]: (data && data[fieldNames.country]) || "",
    [fieldNames.phoneCode]: (data && data[fieldNames.phoneCode]) || "",
    [fieldNames.cellularPhone]: (data && data[fieldNames.cellularPhone]) || "",
    [fieldNames.pagerPhone]: (data && data[fieldNames.pagerPhone]) || "",
    [fieldNames.homeEmailAddr]: (data && data[fieldNames.homeEmailAddr]) || "",
    [fieldNames.homePhone]: (data && data[fieldNames.homePhone]) || "",
    [fieldNames.payrollId]: (data && data[fieldNames.payrollId]) || "",
    [fieldNames.emergencyContactFlag]:
      (data && data[fieldNames.emergencyContactFlag]) || "",
    [fieldNames.defaultBranchId]: (data && data[fieldNames.districtId]) || "",
    [fieldNames.assignedBranchList]:
      (data && data[fieldNames.districtList]) || "",
    [fieldNames.activeBranchList]:
      (data && data[fieldNames.localEmployeeDistrictList]) || "",
    [fieldNames.securityGroupId]:
      (data && data[fieldNames.securityGroupId]) || "",
    [fieldNames.loggedInEmployeeId]:
      (data && data[fieldNames.loggedInEmployeeId]) || "",
    [fieldNames.localeId]: (data && data[fieldNames.localeId]) || ""
  };
  return formData;
};

export const isTerminateIntent = (securityGroupId, endDate) => {
  const isTerminatedGroup =
    String(securityGroupId ?? "") === String(SECURITY_GROUPS.TERMINATED_USER);
  return isTerminatedGroup || Boolean(endDate);
};

export const findTerminatedJobTitleValue = (jobTitleOptions = []) => {
  const match = jobTitleOptions.find((opt) => {
    const idMatch = String(opt?.value) === TERMINATED_JOB_TITLE_ID;
    const label = String(opt?.label || "")
      .trim()
      .toLowerCase();
    return idMatch || label === "terminated";
  });
  return match ? match.value : null;
};

export const isTerminatedJobTitle = (jobTitleId, jobTitleOptions = []) => {
  const terminatedValue = findTerminatedJobTitleValue(jobTitleOptions);
  if (terminatedValue == null) {
    return String(jobTitleId) === TERMINATED_JOB_TITLE_ID;
  }
  return String(jobTitleId) === String(terminatedValue);
};

export const formattedDate = (value) => {
  return dayjs(value).format(dateFormat.universalFormat);
};

export const isTerminatedSecurityGroupId = (groupId) => {
  const id = String(groupId ?? "").trim();
  if (!id) return false;
  return (
    id === String(SECURITY_GROUPS.TERMINATED_USER) ||
    Number(id) === Number(SECURITY_GROUPS.TERMINATED_USER)
  );
};

export const isTerminatedJobTitleLabel = (label) =>
  String(label ?? "")
    .trim()
    .toLowerCase() === String(terminatedCode).trim().toLowerCase();

export const filterActiveJobTitleOptions = (options = []) =>
  options.filter((option) => !isTerminatedJobTitleLabel(option?.label));

export const isTerminatedJobTitleId = (jobTitleId, options = []) => {
  if (jobTitleId == null || jobTitleId === "") return false;
  const option = options.find(
    (item) => String(item?.value) === String(jobTitleId)
  );
  return isTerminatedJobTitleLabel(option?.label);
};

export const getRehireDateValues = (now = dayjs()) => ({
  startDate: now,
  employmentStartDate: formattedDate(now),
  employmentEndDate: ""
});

export const getCountryPhoneCode = (value) => {
  return value?.split("-");
};

export const convertToString = (arrayValue) => {
  return arrayValue?.toString().replace(/,/g, "|");
};

export const setDateValue = async (value) => {
  return dayjs(
    dayjs(value, {
      strict: true
    }).toDate()
  );
};

let regexOnlyString = /^[a-zA-Z'\s]*$/;
let regexOnlyNumber = /^[0-9]*$/;
let regexEmail = /^[^@\s]*$/;
let regexFullEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EMAIL_LOCAL_PART_MAX_LENGTH = 35;

export const isTextValid = (str) => regexOnlyString.test(str);
export const isNumValid = (num) => regexOnlyNumber.test(num);
export const isEmailValid = (str) => regexEmail.test(str);
export const isFullEmailValid = (str) => regexFullEmail.test(str);

/**
 * Parse a pasted business email into local part + domain.
 * Domain must match an allowed option (case-insensitive).
 * Returns type: "local" | "full" | "invalid" | "invalidDomain"
 */
export const parsePastedBusinessEmail = (pastedText, domainOptions = []) => {
  const value = pastedText?.trim() || "";
  if (!value.includes("@")) {
    return { type: "local" };
  }

  if (!isFullEmailValid(value)) {
    return { type: "invalid" };
  }

  const atIndex = value.lastIndexOf("@");
  const localPart = value.slice(0, atIndex).replace(/\s+/g, "");
  const domainPart = value.slice(atIndex + 1).replace(/\s+/g, "");

  if (
    !localPart ||
    localPart.length > EMAIL_LOCAL_PART_MAX_LENGTH ||
    !isEmailValid(localPart)
  ) {
    return { type: "invalid" };
  }

  const matchedDomain = domainOptions.find(
    (opt) =>
      String(opt?.value || "")
        .trim()
        .toLowerCase() === domainPart.toLowerCase()
  );

  if (!matchedDomain) {
    return { type: "invalidDomain", localPart };
  }

  return {
    type: "full",
    localPart,
    domain: matchedDomain.value
  };
};

export const composeBusinessEmail = (localPart, domain) => {
  const local = String(localPart ?? "").replace(/\s+/g, "").trim();
  const host = String(domain ?? "").trim();
  if (!local || !host) return "";
  return `${local}@${host}`;
};

export const normalizeBusinessEmail = (value) => {
  const text = String(value ?? "").trim().toLowerCase();
  if (!text) return "";
  const at = text.lastIndexOf("@");
  if (at < 0) return text.replace(/\s+/g, "");
  const local = text.slice(0, at).replace(/\s+/g, "");
  const host = text.slice(at + 1).replace(/\s+/g, "");
  return host ? `${local}@${host}` : local;
};

export const isEmailUniquenessError = (row) => {
  if (!row || typeof row !== "object") return false;
  const errorCode = String(row.error_code || "").trim();
  if (errorCode.startsWith("EMAIL_EXISTS")) return true;
  const error = Number(row.error);
  return EMAIL_UNIQUENESS_CODES.includes(error);
};

export const isPayrollUniquenessError = (row) => {
  if (!row || typeof row !== "object") return false;
  const errorCode = String(row.error_code || "").trim();
  if (errorCode === "PAYROLL_EXISTS") return true;
  return Number(row.error) === PAYROLL_EXISTS_CODE;
};

export const isEmployeeUniquenessError = (row) =>
  isEmailUniquenessError(row) || isPayrollUniquenessError(row);

const firstPresent = (obj, keys) => {
  for (const key of keys) {
    if (obj?.[key] != null && obj[key] !== "") return obj[key];
  }
  return undefined;
};

const normalizeText = (value) =>
  String(value ?? "")
    .replace(/\0/g, "")
    .trim()
    .toLowerCase();

const END_DATE_KEYS = [
  "employment_end_date",
  "Employment_End_Date",
  "employmentEndDate",
  "end_date"
];

const isRealEndDate = (date) =>
  date instanceof Date &&
  !Number.isNaN(date.getTime()) &&
  date.getUTCFullYear() >= 1971;

const hasEmploymentEndDate = (value) => {
  if (value == null || value === false) return false;
  if (typeof value === "number") {
    if (!Number.isFinite(value) || value === 0) return false;
    const date = value > 1e12 ? new Date(value) : new Date(value * 1000);
    return isRealEndDate(date);
  }
  if (typeof value === "string") {
    const text = value.trim();
    if (!text || /^(null|undefined|invalid date|0000-00-00)$/i.test(text)) {
      return false;
    }
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) return isRealEndDate(parsed);
    return true;
  }
  if (typeof value === "object") {
    if (typeof value.toDate === "function") {
      try {
        return isRealEndDate(value.toDate());
      } catch {
        return true;
      }
    }
    const seconds = value.seconds ?? value._seconds;
    if (seconds != null && seconds !== "") {
      return isRealEndDate(new Date(Number(seconds) * 1000));
    }
    if (value instanceof Date) return isRealEndDate(value);
  }
  return false;
};

const isTerminatedSecurityGroup = (match) =>
  isTerminatedSecurityGroupId(
    firstPresent(match, ["security_group_id", "Security_Group_Id"])
  );

const isTerminatedMatch = (match) => {
  if (isTerminatedSecurityGroup(match)) return true;
  if (hasEmploymentEndDate(firstPresent(match, END_DATE_KEYS))) return true;
  const code = normalizeText(firstPresent(match, ["code", "Code"]));
  const descr = normalizeText(firstPresent(match, ["descr", "Descr"]));
  const status = normalizeText(
    firstPresent(match, ["employee_status_code", "Employee_Status_Code"])
  );
  if (code === normalizeText(terminatedCode) || code.startsWith("terminated")) {
    return true;
  }
  if (descr.includes("terminated")) return true;
  if (status.includes("terminated") || status === "term") return true;
  return false;
};

export const getEmailUniquenessCode = (match) => {
  if (isTerminatedMatch(match)) return empMsgCode.emailExistsTerminated;
  const activeFlag = String(
    firstPresent(match, ["active_flag", "Active_Flag"]) ?? ""
  ).toUpperCase();
  if (activeFlag === N_CHECKER) return empMsgCode.emailExistsInactive;
  return empMsgCode.emailExists;
};

export const pickUniquenessMatch = (matches = []) => {
  const rank = (item) => {
    const code = getEmailUniquenessCode(item);
    if (code === empMsgCode.emailExists) return 1;
    if (code === empMsgCode.emailExistsTerminated) return 2;
    return 3;
  };
  return [...matches].sort((a, b) => rank(a) - rank(b))[0];
};
